import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import './App.css';
import { ReactComponent as CoralLogoSvg } from './coral-logo.svg';
import { payers, Payer, COVERAGE_OPTIONS, STATE_OPTIONS, TRANSACTION_OPTIONS } from './data/payers';

const CoralHeader = () => (
  <div className="coral-header">
    <div className="coral-header-inner">
      <CoralLogoSvg className="coral-logo" />
      <span className="coral-divider">|</span>
      <span className="coral-title">Payer Network</span>
    </div>
  </div>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill="#0D9488" />
    <path d="M7.5 12.5L10.5 15.5L16.5 9.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M8 8L16 16M16 8L8 16" stroke="#D4D4D8" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ClaimBadge: React.FC<{ type: string }> = ({ type }) => (
  <span className="claim-badge">{type}</span>
);

const coverageIconMap: Record<string, string> = {
  dental: '🦷',
  medical: '🏥',
  vision: '👁',
};

function getAvatarColor(id: string): string {
  const colors = ['#0D9488','#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#6366f1','#be185d','#0d9488','#ea580c','#003DA6','#0057A8','#006747'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name: string): string {
  return name.split(/[\s\-&()]+/).filter(w => w.length > 0).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function OtherNamesPopover({ names, count, payerName }: { names: string[]; count: number; payerName: string }) {
  const filtered = names.filter(n => n.toLowerCase() !== payerName.toLowerCase());
  if (filtered.length === 0) return null;
  return (
    <span className="other-names-wrap">
      <span className="other-names">{filtered.length} other names</span>
      <div className="names-popover">
        <div className="names-popover-inner">
          <div className="names-popover-header">Other names</div>
          <div className="names-popover-list">
            {filtered.map((n, i) => (
              <div key={i} className="names-popover-item">{n}</div>
            ))}
          </div>
        </div>
      </div>
    </span>
  );
}

const ROW_HEIGHT = 58;
const OVERSCAN = 10;

function PayerRow({ payer, index, style }: { payer: Payer; index: number; style: React.CSSProperties }) {
  const badges: string[] = [];
  if (payer.claim837P) badges.push('P');
  if (payer.claim837I) badges.push('I');
  if (payer.claim837D) badges.push('D');

  return (
    <div className={`payer-row ${index % 2 === 1 ? 'alt' : ''}`} style={style}>
      <div className="payer-cell name-cell">
        <div className="payer-avatar" style={{ backgroundColor: getAvatarColor(payer.stediId) }}>
          <span>{getInitials(payer.name)}</span>
        </div>
        <div className="payer-details">
          <div className="payer-name">{payer.name}</div>
          <div className="payer-meta">
            <span className="payer-id">{payer.id}</span>
            {payer.coverageTypes.length > 0 && (
              <>
                <span className="meta-sep">|</span>
                {payer.coverageTypes.map(ct => (
                  <span key={ct} className="coverage-icon" title={ct}>
                    {coverageIconMap[ct] || ct}
                  </span>
                ))}
              </>
            )}
            {payer.otherNames > 0 && (
              <>
                <span className="meta-sep">|</span>
                <OtherNamesPopover names={payer.namesList} count={payer.otherNames} payerName={payer.name} />
              </>
            )}
          </div>
        </div>
      </div>
      <div className="payer-cell tx-cell">{payer.eligibility ? <CheckIcon /> : <XIcon />}</div>
      <div className="payer-cell tx-cell">{payer.cob ? <CheckIcon /> : <XIcon />}</div>
      <div className="payer-cell tx-cell claim-sub-cell">
        {badges.length > 0 ? (
          <div className="claim-badges">{badges.map(t => <ClaimBadge key={t} type={t} />)}</div>
        ) : <XIcon />}
      </div>
      <div className="payer-cell tx-cell">{payer.claimAck ? <CheckIcon /> : <XIcon />}</div>
      <div className="payer-cell tx-cell">{payer.claimPayment ? <CheckIcon /> : <XIcon />}</div>
      <div className="payer-cell tx-cell">{payer.claimAttachments ? <CheckIcon /> : <XIcon />}</div>
      <div className="payer-cell tx-cell">{payer.realtimeClaim ? <CheckIcon /> : <XIcon />}</div>
    </div>
  );
}

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showCoverageFilter, setShowCoverageFilter] = useState(false);
  const [showStateFilter, setShowStateFilter] = useState(false);
  const [showTxFilter, setShowTxFilter] = useState(false);
  const [coverageFilter, setCoverageFilter] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [stateSearch, setStateSearch] = useState('');
  const [txFilter, setTxFilter] = useState<string[]>([]);

  // Virtualized scroll
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(800);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => setContainerHeight(el.clientHeight));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) setScrollTop(scrollRef.current.scrollTop);
  }, []);

  const filteredPayers = useMemo(() => {
    return payers.filter(p => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.id.toLowerCase().includes(q)) return false;
      }
      if (coverageFilter.length > 0) {
        if (!coverageFilter.some(c => p.coverageTypes.includes(c))) return false;
      }
      if (stateFilter.length > 0) {
        const isNational = p.operatingStates.includes('NATIONAL');
        if (!isNational && !stateFilter.some(s => p.operatingStates.includes(s))) return false;
      }
      if (txFilter.length > 0) {
        for (const tx of txFilter) {
          if (tx === 'eligibility' && !p.eligibility) return false;
          if (tx === 'cob' && !p.cob) return false;
          if (tx === 'claimSubmission' && !p.claim837P && !p.claim837I && !p.claim837D) return false;
          if (tx === 'claimAck' && !p.claimAck) return false;
          if (tx === 'claimPayment' && !p.claimPayment) return false;
          if (tx === 'claimAttachments' && !p.claimAttachments) return false;
          if (tx === 'realtimeClaim' && !p.realtimeClaim) return false;
        }
      }
      return true;
    });
  }, [searchQuery, coverageFilter, stateFilter, txFilter]);

  const stats = useMemo(() => ({
    eligibility: filteredPayers.filter(p => p.eligibility).length,
    cob: filteredPayers.filter(p => p.cob).length,
    claimSubmission: filteredPayers.filter(p => p.claim837P || p.claim837I || p.claim837D).length,
    claimAck: filteredPayers.filter(p => p.claimAck).length,
    claimPayment: filteredPayers.filter(p => p.claimPayment).length,
    claimAttachments: filteredPayers.filter(p => p.claimAttachments).length,
    realtimeClaim: filteredPayers.filter(p => p.realtimeClaim).length,
  }), [filteredPayers]);

  const totalHeight = filteredPayers.length * ROW_HEIGHT;
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIdx = Math.min(filteredPayers.length, Math.ceil((scrollTop + containerHeight) / ROW_HEIGHT) + OVERSCAN);
  const visiblePayers = filteredPayers.slice(startIdx, endIdx);

  const toggleCoverage = (c: string) => setCoverageFilter(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const toggleState = (s: string) => setStateFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleTx = (t: string) => setTxFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const filteredStates = STATE_OPTIONS.filter(s =>
    s.label.toLowerCase().includes(stateSearch.toLowerCase()) || s.key.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const closeDropdowns = () => { setShowCoverageFilter(false); setShowStateFilter(false); setShowTxFilter(false); setStateSearch(''); };

  return (
    <div className="app" onClick={(e) => { if (!(e.target as HTMLElement).closest('.filter-dropdown-wrap')) closeDropdowns(); }}>
      <CoralHeader />
      {/* Toolbar */}
      <div className="toolbar">
        <div className="search-bar">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input type="text" placeholder="Search by name, short name, or alias" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="filter-bar">
          <span className="filter-label">Filter by</span>
          {/* Coverage Type */}
          <div className="filter-dropdown-wrap">
            <button className={`filter-btn ${coverageFilter.length > 0 ? 'active' : ''}`} onClick={() => { setShowCoverageFilter(!showCoverageFilter); setShowStateFilter(false); setShowTxFilter(false); }}>
              <span className="plus">+</span> Coverage Type
              {coverageFilter.length > 0 && <span className="filter-count">{coverageFilter.length}</span>}
            </button>
            {showCoverageFilter && (
              <div className="dropdown">
                {COVERAGE_OPTIONS.map(c => (
                  <label key={c.key} className="dropdown-item">
                    <input type="checkbox" checked={coverageFilter.includes(c.key)} onChange={() => toggleCoverage(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          {/* Operating States */}
          <div className="filter-dropdown-wrap">
            <button className={`filter-btn ${stateFilter.length > 0 ? 'active' : ''}`} onClick={() => { setShowStateFilter(!showStateFilter); setShowCoverageFilter(false); setShowTxFilter(false); }}>
              <span className="plus">+</span> Operating States
              {stateFilter.length > 0 && <span className="filter-count">{stateFilter.length}</span>}
            </button>
            {showStateFilter && (
              <div className="dropdown dropdown-states">
                <input
                  type="text"
                  className="dropdown-search"
                  placeholder="Search states..."
                  value={stateSearch}
                  onChange={e => setStateSearch(e.target.value)}
                  autoFocus
                />
                <div className="dropdown-scroll">
                  {filteredStates.map(s => (
                    <label key={s.key} className="dropdown-item">
                      <input type="checkbox" checked={stateFilter.includes(s.key)} onChange={() => toggleState(s.key)} />
                      {s.label} <span className="state-code">{s.key}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Transactions */}
          <div className="filter-dropdown-wrap">
            <button className={`filter-btn ${txFilter.length > 0 ? 'active' : ''}`} onClick={() => { setShowTxFilter(!showTxFilter); setShowCoverageFilter(false); setShowStateFilter(false); }}>
              <span className="plus">+</span> Transactions
              {txFilter.length > 0 && <span className="filter-count">{txFilter.length}</span>}
            </button>
            {showTxFilter && (
              <div className="dropdown">
                {TRANSACTION_OPTIONS.map(t => (
                  <label key={t.key} className="dropdown-item">
                    <input type="checkbox" checked={txFilter.includes(t.key)} onChange={() => toggleTx(t.key)} />
                    <span>{t.label} <span className="tx-code">{t.code}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div className="table-header-wrap">
        <div className="table-header">
          <div className="payer-cell name-cell header-name-cell">
            <span className="total-count">{filteredPayers.length.toLocaleString()} total payers</span>
          </div>
          <div className="payer-cell tx-cell header-tx-cell"><span className="col-count"><strong>{stats.eligibility.toLocaleString()}</strong> payers</span></div>
          <div className="payer-cell tx-cell header-tx-cell"><span className="col-count"><strong>{stats.cob}</strong> payers</span></div>
          <div className="payer-cell tx-cell header-tx-cell"><span className="col-count"><strong>~{stats.claimSubmission.toLocaleString()}</strong> payers</span></div>
          <div className="payer-cell tx-cell header-tx-cell"><span className="col-count"><strong>{stats.claimAck.toLocaleString()}</strong> payers</span></div>
          <div className="payer-cell tx-cell header-tx-cell"><span className="col-count"><strong>{stats.claimPayment.toLocaleString()}</strong> payers</span></div>
          <div className="payer-cell tx-cell header-tx-cell"><span className="col-count"><strong>{stats.claimAttachments}</strong> payers</span></div>
          <div className="payer-cell tx-cell header-tx-cell"><span className="col-count"><strong>{stats.realtimeClaim}</strong> payers</span></div>
        </div>
        <div className="table-subheader">
          <div className="payer-cell name-cell"><span className="col-label">Name &amp; ID</span></div>
          <div className="payer-cell tx-cell"><span className="col-label">Eligibility checks</span><span className="col-sublabel">270/271</span></div>
          <div className="payer-cell tx-cell"><span className="col-label">COB check</span><span className="col-sublabel">271</span></div>
          <div className="payer-cell tx-cell"><span className="col-label">Claim submission</span><span className="col-sublabel">837P, 837I, 837D</span></div>
          <div className="payer-cell tx-cell"><span className="col-label">Claim acknowledgment</span><span className="col-sublabel">277CA</span></div>
          <div className="payer-cell tx-cell"><span className="col-label">Claim payment</span><span className="col-sublabel">835 ERA</span></div>
          <div className="payer-cell tx-cell"><span className="col-label">Claim attachments</span><span className="col-sublabel">275</span></div>
          <div className="payer-cell tx-cell"><span className="col-label">Real-time claim status</span><span className="col-sublabel">276/277</span></div>
        </div>
      </div>

      {/* Virtualized Table Body */}
      <div className="table-body" ref={scrollRef} onScroll={handleScroll}>
        <div style={{ height: totalHeight, position: 'relative' }}>
          {visiblePayers.map((payer, i) => (
            <PayerRow
              key={payer.stediId}
              payer={payer}
              index={startIdx + i}
              style={{
                position: 'absolute',
                top: (startIdx + i) * ROW_HEIGHT,
                left: 0,
                right: 0,
                height: ROW_HEIGHT,
              }}
            />
          ))}
        </div>
        {filteredPayers.length === 0 && (
          <div className="no-results">No payers match your search.</div>
        )}
      </div>
    </div>
  );
}

export default App;
